import { useEffect, useCallback, useContext } from "react";
import { toast } from "react-toastify";
import { Routes, Route, Navigate } from "react-router-dom";
import {
  DefaultLayout,
  PrivateRoute,
  TokenComponent,
} from "@client/shared/components";
import Notifications from "./notifications";
import Box from "@mui/material/Box";
import { getUserData, getRCAccountId, getTokenData } from "@client/utils";
import { RC_USER_ID } from "@client/shared/constants";
import NotificationWizard from "./notifications/create";
import WhitelistComponent from "./whitelist";
import EditNotification from "./notifications/edit";
import { AppService } from "../shared/services";
import { AppContext } from "@client/shared/contexts";
import { DEFAULT_ERROR_MESSAGE } from "@client/shared/constants";
import UserPermissions from "./user-permissions";
import AccessDenied from './access-denied';

const App = () => {
  const { setAppData } = useContext(AppContext);
  const userData = (getUserData() || {}).user || {};
  const tokenData = getTokenData();
  const hasAppAccess = userData?.roleAccess?.app_access;
  const isWhitListVisible = !!(
    userData &&
    userData?.role?.name === "Admin" &&
    !!RC_USER_ID.includes(userData?.rc_account_id)
  );

  const isRoleScreenVisible = tokenData?.isSuperAdmin;
  // const isWhitListVisible = false;
  let drawerList = [
    {
      icon: <img src="/assets/notification-bell.svg" alt="" />,
      activeIcon: <img src="/assets/notification-bell.svg" alt="" />,
      to: "/notifications",
      label: "Notifications",
      canNavigateTo: () => {
        const currentUrl = window.location.href;
        const notificationUrlRegExp = new RegExp("/notifications$");
        return !notificationUrlRegExp.test(currentUrl);
      },
    },
  ];
  if (isWhitListVisible) {
    drawerList.push({
      icon: <img src="/assets/whitelist-grey.svg" alt="" />,
      activeIcon: <img src="/assets/whitelist.svg" alt="" />,
      to: "/whitelist",
      label: "Whitelist",
      canNavigateTo: () => {
        const currentUrl = window.location.href;
        const notificationUrlRegExp = new RegExp("/whitelist$");
        return !notificationUrlRegExp.test(currentUrl);
      },
    });
  }
  if (isRoleScreenVisible) {
    drawerList.push({
      icon: <img src="/assets/users-purple.svg" alt="" />,
      activeIcon: <img src="/assets/users-purple.svg" alt="" />,
      to: "/user-permissions",
      label: "User Permissions",
      canNavigateTo: () => {
        const currentUrl = window.location.href;
        const notificationUrlRegExp = new RegExp("/user-permissions$");
        return !notificationUrlRegExp.test(currentUrl);
      },
    });
  }

  // Data Fetchers
  const fetchSiteList = useCallback(async () => {
    setAppData("loadedSites", false);
    const { data, error } = await AppService.getAllSites(getRCAccountId());
    if (error) {
      toast.error(DEFAULT_ERROR_MESSAGE);
    } else {
      setAppData("sites", data);
      setAppData("loadedSites", true);
    }
  }, []);

  const fetchUserList = useCallback(async () => {
    setAppData("loadedUsers", false);
    const { data, error } = await AppService.getAllUsers(getRCAccountId());
    if (error) {
      toast.error(DEFAULT_ERROR_MESSAGE);
    } else {
      setAppData("users", data);
      setAppData("loadedUsers", true);
    }
  }, []);

  const fetchCountries = useCallback(async () => {
    setAppData("loadedCountries", false);
    const { data, error } = await AppService.getCountryList();
    if (error) {
      toast.error(DEFAULT_ERROR_MESSAGE);
    } else {
      if (data && data.length > 0) {
        data.sort((countryA, countryB) =>
          countryA.name > countryB.name ? 1 : -1
        );
        setAppData("countries", data);
        setAppData("loadedCountries", true);
      }
    }
  }, []);

  const fetchTeams = useCallback(async () => {
    setAppData("loadedTeams", false);
    const { data, error } = await AppService.getAllTeams(getRCAccountId());
    setAppData("loadedTeams", true);
    if (error) {
      toast.error(DEFAULT_ERROR_MESSAGE);
    } else {
      setAppData("teams", data);
    }
  }, []);

  const fetchTimezone = useCallback(async () => {
    setAppData("loadedTimezones", false);
    const { data, error } = await AppService.getTimezoneList();
    setAppData("loadedTimezone", true);
    if (error) {
      toast.error(DEFAULT_ERROR_MESSAGE);
    } else {
      if (data && data.length > 0) {
        data.sort((countryA, countryB) =>
          countryA.name > countryB.name ? 1 : -1
        );
        setAppData("timezones", data);
      }
    }
  }, []);

  const fetchExtension = useCallback(async () => {
    setAppData("loadedExtension", false);
    const { data, error } = await AppService.getExtensionList();
    setAppData("loadedExtension", true);
    if (error) {
      toast.error(DEFAULT_ERROR_MESSAGE);
    } else {
      setAppData("extensions", data);
    }
  }, []);

  useEffect(() => {
    if (hasAppAccess) {
      fetchSiteList();
      fetchUserList();
      fetchTeams();
      fetchCountries();
      fetchTimezone();
      fetchExtension();
    }
  }, []);

  return (
    <Box>
      <DefaultLayout drawerList={drawerList}></DefaultLayout>
      <Box sx={{ marginLeft: "250px", marginTop: "80px" }}>
        <Routes>
          <Route path="/token" element={<TokenComponent />} />
          <Route
            path="/notifications"
            element={<PrivateRoute element={<Notifications />} />}
          />
          <Route path="/" element={<Navigate to="/notifications" />} />
          <Route
            path="/notifications/create"
            element={<PrivateRoute element={<NotificationWizard />} />}
          />
          {isWhitListVisible && (
            <Route
              path="/whitelist"
              element={<PrivateRoute element={<WhitelistComponent />} />}
            />
          )}
          <Route
            path="/notifications/:id/edit"
            element={<PrivateRoute element={<EditNotification />} />}
          />
          {isRoleScreenVisible && (
            <Route
              path="/user-permissions"
              element={<PrivateRoute element={<UserPermissions />} />}
            />
          )}
          <Route path="/access-denied" element={<AccessDenied open={true} />} />
        </Routes>
      </Box>
    </Box>
  );
};

export default App;
